import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import NeoOTP from '../../components/FormComponents/NeoOTP'
import NeoSuccess from '../../components/FormComponents/NeoSuccess'
import LayoutWithAccountCard from '../../layout/Layout/layout-account-card'
import NeoButton from '../../components/NeoButton/NeoButton'
import { carouselCurrentItemSelector } from '../../selectors'
import NeoInput from '../../components/NeoInput/NeoInput'
import InputError from '../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import AccountDisplay from '../../components/account-display/account-display'
import CardLessService from '../../services/cardless.service'
import { CardLessSchema } from '../../schemas/send-to-mobile'
import TermsAndCondition from '../../components/TermsAndConditions/TermsAndCondition'
import config from 'custom/config/environment'
import { useTranslation } from 'react-i18next'
import { CARDLESS_CASH_KEYS, SIDENAV_KEYS, VALIDATION_MESSAGES } from '../../utils/translation-constants'

export default function GenerateToken() {
  const history = useHistory()
  const [stage, setStage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [verifyDetails, setVerifyDetails] = useState({})
  const [otp, setOtp] = useState('')
  const {t} = useTranslation()
  const [formValues, setFormValues] = useState({
    remarks: '',
    mobileNumber: '',
    expiry: '',
    transferAmount: '',
    maxTransferAmount: undefined,
  })
  const currentAccountDetail = useSelector(carouselCurrentItemSelector)

  const onVerify = useCallback(
    async (values) => {
      try {
        if(values.transferAmount==='0.00' || values.transferAmount==='.00'){
          return
        }
        if(values.expiry==="0"){
          return
        }
        setLoading(true)
        const req = {
          amount: values.transferAmount,
          mobileNumber: values.mobileNumber,
          expiry: values.expiry,
          srcAccount: currentAccountDetail.accountNo,
          srcAccBranch: currentAccountDetail.brnCode,
          srcAccCcy: currentAccountDetail.currency,
          txnCurrency: currentAccountDetail.currency,
          remarks: values.remarks,
        }
        setFormValues(values)
        const response = await CardLessService.cardlessGenTokenVerify(req)
        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setVerifyDetails(response.data)
          setLoading(false)
          setStage(2)
        }
      } catch (err) {
        setLoading(false)
      }
    },
    [currentAccountDetail],
  )

  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const req = {
        amount: formValues.transferAmount,
        mobileNumber: formValues.mobileNumber,
        expiry: formValues.expiry,
        srcAccount: currentAccountDetail.accountNo,
        srcAccBranch: currentAccountDetail.brnCode,
        srcAccCcy: currentAccountDetail.currency,
        txnCurrency: currentAccountDetail.currency,
        remarks: formValues.remarks,
        otp: otp,
      }
      const response = await CardLessService.cardlessGenToken(req)
      if (response.status === 200) {
        setLoading(false)
        setMessage(response.data['message'])
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [currentAccountDetail, formValues, otp])

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/accounts/cardless-cash' />
      default:
        return renderForm()
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderForm() {
    return (
      <>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={CardLessSchema}
          onSubmit={onVerify}>
          {({ isSubmitting, handleChange, errors, values, touched, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    text={t(CARDLESS_CASH_KEYS.RECEIVER_MOBILE_NO)}
                    onChange={(value) => {
                      setFieldValue('mobileNumber', value?.value)
                    }}
                    mode='onlyNumber'
                    name='mobileNumber'
                  />

                  <InputError text={errors.mobileNumber} touched={touched.mobileNumber} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    text={t(CARDLESS_CASH_KEYS.AMOUNT)}
                    name='amount'
                    onChange={(values) => {
                      console.log(values)
                      setFieldValue('transferAmount', values.value)
                      setFieldValue('maxTransferAmount', values.floatValue)
                    }}
                    mode='amount'
                  />
                  <InputError text={errors.transferAmount} touched={touched.transferAmount} />
                  <InputError
                    text={t(CARDLESS_CASH_KEYS.AMOUNT_VALIDITY)}
                    touched={values.maxTransferAmount < 0}
                  />
                  <InputError
                    text={t(VALIDATION_MESSAGES.AMOUNT_VALID)}
                    touched={values.transferAmount === '0.00' || values.transferAmount==='.00'}
                  />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='3'
                    text={t(CARDLESS_CASH_KEYS.VALIDITY_HRS)}
                    onChange={(value) => {
                      setFieldValue('expiry', value?.value)
                    }}
                    mode='onlyNumber'
                    name='expiry'
                    minLength={1}
                    maxLength={2}
                  />
                  <InputError text={errors.expiry} touched={touched.expiry} />
                  <InputError
                    text={t(VALIDATION_MESSAGES.VALIDITY_VALUE)}
                    touched={values.expiry === '0'}
                  />
                </div>

                <div className='form-group col-12'>
                  <NeoInput tabIndex='4' text={t(CARDLESS_CASH_KEYS.REMARKS)} onChange={handleChange} name='remarks' />
                  <InputError text={errors.remarks} touched={touched.remarks} />
                </div>
              </div>
              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton tabIndex='5' type='submit' mode='newDesign' text={t(SIDENAV_KEYS.TOUCH_ID_KEYS.SUBMIT)} mode='newDesign' />
                )}
              </div>
              <div className='back-cancel-btn-container'>
              <button className='back-cancel-btn btn btn-sm new-design-warning-text-color' tabIndex={'5'} onClick={() => history.goBack()}>
              {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
              </button>
              </div>
            </Form>
          )}
        </Formik>
      </>
    )
  }

  function renderConfirmation() {
    return (
      <>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <div className='d-flex flex-column confirmation'>
          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.RECEIVER_MOBILE_NO)}</h6>
            <h6 className='value'>{`${config.COUNTRY_CODE}${formValues?.mobileNumber}`}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.AMOUNT)}</h6>
            <h6 className='value'>{`${currentAccountDetail?.currency} ${formValues?.amount}`}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.CHARGES)}</h6>
            <h6 className='value'>{verifyDetails?.charges}</h6>
          </div>

          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.VALIDATIY)}</h6>
            <h6 className='value'>{formValues?.expiry} hrs</h6>
          </div>
          {/* <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.TRANSACTION_CURRENCY)}</h6>
            <h6 className='value'>{currentAccountDetail?.currency}</h6>
          </div> */}

          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.REMARKS)}</h6>
            <h6 className='value'>{formValues?.remarks}</h6>
          </div>

          <div className='form-group col-12'>
            <TermsAndCondition module='CARDLESSCASHOUT' />
          </div>

          <div className='mt-3'>
            {loading ? (
              renderLoading()
            ) : (
              <NeoButton
                onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
                type='button'
                mode='newDesign'
                text={t(SIDENAV_KEYS.TOUCH_ID_KEYS.CONFIRM)}
                mode='newDesign'
              />
            )}
          </div>
          <div className='back-cancel-style' onClick={() => history.goBack()}>
            {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
          </div>
        </div>
      </>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='ACCOUNT_CARDLESS_GENERATE_TOKEN'>
      <div className='new-design-form-style'>
        <div className='heading-style'>{t(CARDLESS_CASH_KEYS.GENERATE_TOKEN)}</div>
        {renderSwitch()}
      </div>
    </LayoutWithAccountCard>
  )
}
